import React, { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { MessageCircle, X, Send, Sparkles, Trash2, ArrowRight } from "lucide-react";
import { whatsappLink, WHATSAPP_MESSAGES } from "../utils/whatsapp";

const WHATSAPP_NUMBER = "+447486160965";
const BOT_NAME = "Kinews Assistant";

const INITIAL_GREETING = "Hi there! 👋 I'm Kinews Assistant. Before we begin, may I know your name?";

const SUGGESTIONS = [
  { label: "📸 Studio Booking", value: "booking" },
  { label: "🏢 Studio Rentals", value: "rentals" },
  { label: "💍 Event Photography", value: "events" },
  { label: "🖨️ Prints", value: "printing" },
  { label: "🖼️ Shop Frames & Personalised Items", value: "shop" },
  { label: "💬 Chat with our team", value: "human" },
];

const RESPONSES = (name) => ({
  booking: {
    text: `Thanks ${name}! You can book a professional studio session directly on our website.`,
    link: "/book",
    linkText: "Book a Session"
  },
  rentals: {
    text: `Need the space for your own project, ${name}? We offer flexible studio rental options.`,
    link: "/rentals",
    linkText: "View Rental Rates"
  },
  events: {
    text: `Looking for photography for your big day, ${name}? We have special packages for weddings and location events.`,
    link: "/packages",
    linkText: "Event Packages"
  },
  shop: {
    text: `You'll love our collection, ${name}! Browse our exclusive frames and personalised items in the Shop.`,
    link: "/shop",
    linkText: "Shop Frames & Gifts"
  },
  printing: {
    text: `We offer high-quality prints for flyers, banners, business cards, etc, ${name}! Check out our services.`,
    link: "/printing",
    linkText: "View Prints"
  },
  human: {
    text: `I'll connect you with a member of our team on WhatsApp right away, ${name}. One moment...`,
    whatsapp: true
  },
  services: {
    text: `Here is everything we offer, ${name}:`,
    actions: [
      { label: "📸 Book Session", link: "/book" },
      { label: "🏢 Studio Rental", link: "/rentals" },
      { label: "💍 Event Photography", link: "/packages" },
      { label: "🖨️ Prints", link: "/printing" },
      { label: "🖼️ Shop Frames & Gifts", link: "/shop" }
    ]
  },
  default: {
    text: `I'm still learning, ${name}! Would you like to check our services or chat with our team?`,
    actions: [
      { label: "Check Services", value: "services_trigger" },
      { label: "Chat with our team", whatsapp: true }
    ]
  }
});

export default function ChatBot() {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([
    { id: 1, type: "bot", text: INITIAL_GREETING }
  ]);
  const [userName, setUserName] = useState("");
  const [inputValue, setInputValue] = useState("");
  const [isTyping, setIsTyping] = useState(false);
  const [isWaitingForMessage, setIsWaitingForMessage] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTo({
        top: scrollRef.current.scrollHeight,
        behavior: "smooth",
      });
    }
  }, [messages, isTyping]);

  const clearChat = () => {
    setMessages([{ id: Date.now(), type: "bot", text: INITIAL_GREETING }]);
    setUserName("");
    setInputValue("");
    setIsTyping(false);
    setIsWaitingForMessage(false);
  };

  const handleSend = (text) => {
    if (!text.trim()) return;

    const userMsg = { id: Date.now(), type: "user", text };
    setMessages((prev) => [...prev, userMsg]);
    setInputValue("");
    setIsTyping(true);

    setTimeout(() => {
      let botResponse = {};
      const lowerText = text.toLowerCase();

      if (!userName) {
        const name = text.trim().split(" ")[0];
        setUserName(name);
        botResponse = { text: `Nice to meet you, ${name}! 😊 How can I help you today?` };
      } else if (isWaitingForMessage) {
        setIsWaitingForMessage(false);
        const encodedMessage = encodeURIComponent(`Hi Kinews Team, my name is ${userName}. I have a question: ${text}`);
        const waLink = `https://wa.me/${WHATSAPP_NUMBER.replace("+", "").replace(/\s/g, "")}?text=${encodedMessage}`;
        
        botResponse = { 
          text: `Got it! I'm connecting you with our team now...`,
          whatsappLink: waLink
        };
        
        setTimeout(() => {
          window.open(waLink, "_blank");
        }, 1500);
      } else {
        const r = RESPONSES(userName);
        
        // 1. Studio Booking
        if ((lowerText.includes("book") || lowerText.includes("session") || lowerText.includes("shoot")) && !lowerText.includes("event") && !lowerText.includes("wedding")) {
          botResponse = r.booking;
        } 
        // 2. Studio Rentals
        else if (lowerText.includes("rental") || lowerText.includes("space") || lowerText.includes("hire") || lowerText.includes("rent")) {
          botResponse = r.rentals;
        }
        // 3. Event Photography
        else if (lowerText.includes("event") || lowerText.includes("wedding") || lowerText.includes("party") || lowerText.includes("package")) {
          botResponse = r.events;
        }
        // 4. Printing
        else if (lowerText.includes("print") || lowerText.includes("flyer") || lowerText.includes("banner") || lowerText.includes("business card") || lowerText.includes("poster")) {
          botResponse = r.printing;
        }
        // 5. Shop
        else if (lowerText.includes("shop") || lowerText.includes("frame") || lowerText.includes("buy") || lowerText.includes("product")) {
          botResponse = r.shop;
        }
        // 5. Human / Team
        else if (lowerText.includes("human") || lowerText.includes("person") || lowerText.includes("whatsapp") || lowerText.includes("talk") || lowerText.includes("team") || lowerText.includes("support")) {
          setIsWaitingForMessage(true);
          botResponse = { text: `Sure! Briefly tell me what you'd like to ask, and I'll connect you with our team right away.` };
        }
        // 6. Services Overview
        else if (lowerText.includes("service") || lowerText.includes("check") || lowerText.includes("option") || lowerText.includes("what")) {
          botResponse = r.services;
        }
        // 7. Small talk & Gratitude & Confirmation
        else if (lowerText === "yes" || lowerText === "yeah" || lowerText === "yup" || lowerText === "sure") {
          botResponse = { 
            text: `Great! What else is on your mind, ${userName}?`,
            actions: [
              { label: "See Services", value: "services_trigger" },
              { label: "Chat with our team", whatsapp: true }
            ]
          };
        }
        else if (lowerText === "no" || lowerText === "nope" || lowerText === "that's all" || lowerText === "nothing") {
          botResponse = { text: `Understood! It was a pleasure chatting with you, ${userName}. Have a wonderful day! ✨` };
        }
        else if (lowerText.includes("thanks") || lowerText.includes("thank you") || lowerText.includes("cheers") || lowerText.includes("awesome")) {
          botResponse = { text: `You're very welcome, ${userName}! 😊 Is there anything else I can help you with?` };
        }
        else if (lowerText.includes("hi") || lowerText.includes("hello") || lowerText.includes("hey")) {
          botResponse = { text: `Hello again, ${userName}! How can I assist you?` };
        } 
        else if (lowerText.includes("hour") || lowerText.includes("open") || lowerText.includes("time")) {
          botResponse = { text: `We're generally open from 9 AM to 6 PM, ${userName}, but you can book sessions online 24/7!` };
        }
        // 8. Fallback
        else {
          botResponse = r.default;
        }
      }

      setMessages((prev) => [...prev, { id: Date.now() + 1, type: "bot", ...botResponse }]);
      setIsTyping(false);
    }, 1000);
  };

  const handleSuggestion = (suggestion) => {
    if (!userName) return;
    if (suggestion.value === "human") {
      handleSend("talk to a person");
    } else {
      handleSend(suggestion.label.split(" ").slice(1).join(" "));
    }
  };

  return (
    <div className="fixed bottom-6 left-6 z-[60]">
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.9, transformOrigin: "bottom left" }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.9 }}
            className="mb-4 w-[calc(100vw-48px)] max-w-[350px] overflow-hidden rounded-[2rem] border border-white/10 bg-zinc-950/90 shadow-2xl backdrop-blur-2xl md:w-[380px] md:max-w-none"
          >
            {/* Header */}
            <div className="relative overflow-hidden bg-zinc-900/50 p-5">
              <div className="absolute -right-4 -top-4 h-24 w-24 rounded-full bg-purple-600/10 blur-2xl" />
              <div className="relative flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="relative">
                    <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-gradient-to-br from-purple-500 to-indigo-600 text-white shadow-lg shadow-purple-500/20">
                      <Sparkles size={20} />
                    </div>
                    <div className="absolute -bottom-0.5 -right-0.5 h-3 w-3 rounded-full border-2 border-zinc-950 bg-green-500" />
                  </div>
                  <div>
                    <h3 className="text-sm font-bold text-white">{BOT_NAME}</h3>
                    <p className="text-[10px] font-medium text-white/40 uppercase tracking-wider">
                      {userName ? `Helping ${userName}` : "Always here to help"}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={clearChat}
                    title="Clear Chat"
                    className="group flex h-8 w-8 items-center justify-center rounded-full bg-white/5 text-white/50 transition-all hover:bg-red-500/10 hover:text-red-400"
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              </div>
            </div>

            {/* Chat Content */}
            <div ref={scrollRef} className="chat-container h-[380px] overflow-y-auto px-5 py-6">
              <div className="space-y-5">
                {messages.map((msg) => (
                  <motion.div
                    key={msg.id}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className={`flex ${msg.type === "bot" ? "justify-start" : "justify-end"}`}
                  >
                    <div className={`flex max-w-[85%] items-end gap-2 ${msg.type === "user" && "flex-row-reverse"}`}>
                      {msg.type === "bot" && (
                        <div className="mb-1 flex h-6 w-6 shrink-0 items-center justify-center rounded-lg bg-purple-500/10 text-purple-400">
                          <Sparkles size={12} />
                        </div>
                      )}
                      <div className="flex flex-col gap-2">
                        <div
                          className={`rounded-2xl px-4 py-2.5 text-[13.5px] leading-relaxed ${
                            msg.type === "bot"
                              ? "rounded-bl-none bg-zinc-900 text-white/90"
                              : "rounded-br-none bg-purple-600 text-white shadow-lg shadow-purple-600/20"
                          }`}
                        >
                          {msg.text}
                        </div>
                        
                        {/* Primary Action Link */}
                        {msg.type === "bot" && msg.link && (
                          <Link
                            to={msg.link}
                            onClick={() => setIsOpen(false)}
                            className="flex items-center justify-between rounded-xl bg-white/5 p-3 text-xs font-semibold text-purple-400 transition hover:bg-white/10"
                          >
                            <span>{msg.linkText}</span>
                            <ArrowRight size={14} />
                          </Link>
                        )}

                        {/* WhatsApp Link */}
                        {msg.type === "bot" && (msg.whatsapp || msg.whatsappLink) && (
                          <a
                            href={msg.whatsappLink || whatsappLink(WHATSAPP_MESSAGES.team)}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center justify-between rounded-xl bg-green-500/10 p-3 text-xs font-semibold text-green-400 transition hover:bg-green-500/20"
                          >
                            <span>{msg.whatsappLink ? "Send Message via WhatsApp" : "Open WhatsApp"}</span>
                            <ArrowRight size={14} />
                          </a>
                        )}

                        {/* Multiple Action Buttons */}
                        {msg.type === "bot" && msg.actions && (
                          <div className="mt-1 grid gap-2">
                            {msg.actions.map((action, i) => {
                              if (action.whatsapp) {
                                return (
                                  <a
                                    key={i}
                                    href={whatsappLink(WHATSAPP_MESSAGES.team)}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="flex items-center justify-between rounded-xl bg-green-500/10 p-3 text-xs font-semibold text-green-400 transition hover:bg-green-500/20"
                                  >
                                    <span>{action.label}</span>
                                    <ArrowRight size={14} />
                                  </a>
                                );
                              }
                              if (action.value === "services_trigger") {
                                return (
                                  <button
                                    key={i}
                                    onClick={() => handleSend("check services")}
                                    className="flex items-center justify-between rounded-xl bg-white/5 p-3 text-xs font-semibold text-purple-400 transition hover:bg-white/10 text-left"
                                  >
                                    <span>{action.label}</span>
                                    <ArrowRight size={14} />
                                  </button>
                                );
                              }
                              return (
                                <Link
                                  key={i}
                                  to={action.link}
                                  onClick={() => setIsOpen(false)}
                                  className="flex items-center justify-between rounded-xl bg-white/5 p-3 text-xs font-semibold text-purple-400 transition hover:bg-white/10"
                                >
                                  <span>{action.label}</span>
                                  <ArrowRight size={14} />
                                </Link>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    </div>
                  </motion.div>
                ))}
                {isTyping && (
                  <div className="flex justify-start">
                    <div className="flex items-end gap-2">
                      <div className="mb-1 flex h-6 w-6 shrink-0 items-center justify-center rounded-lg bg-purple-500/10 text-purple-400">
                        <Sparkles size={12} />
                      </div>
                      <div className="flex gap-1 rounded-2xl rounded-bl-none bg-zinc-900 px-4 py-4">
                        <motion.div
                          animate={{ opacity: [0.3, 1, 0.3], y: [0, -2, 0] }}
                          transition={{ repeat: Infinity, duration: 1 }}
                          className="h-1 w-1 rounded-full bg-white/40"
                        />
                        <motion.div
                          animate={{ opacity: [0.3, 1, 0.3], y: [0, -2, 0] }}
                          transition={{ repeat: Infinity, duration: 1, delay: 0.2 }}
                          className="h-1 w-1 rounded-full bg-white/40"
                        />
                        <motion.div
                          animate={{ opacity: [0.3, 1, 0.3], y: [0, -2, 0] }}
                          transition={{ repeat: Infinity, duration: 1, delay: 0.4 }}
                          className="h-1 w-1 rounded-full bg-white/40"
                        />
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Suggestions */}
              {userName && messages.length <= 3 && !isTyping && (
                <div className="mt-8 grid grid-cols-1 gap-2">
                  <p className="mb-1 px-1 text-[10px] font-bold uppercase tracking-widest text-white/30">Suggestions</p>
                  {SUGGESTIONS.map((s) => (
                    <button
                      key={s.value}
                      onClick={() => handleSuggestion(s)}
                      className="flex items-center justify-between rounded-xl border border-white/5 bg-white/5 px-4 py-3 text-left text-xs text-white/70 transition-all hover:border-purple-500/30 hover:bg-purple-500/10 hover:text-white"
                    >
                      <span>{s.label}</span>
                      <Send size={10} className="opacity-0 transition-all group-hover:opacity-100" />
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Input Area */}
            <div className="bg-zinc-900/30 p-5 pt-0">
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  handleSend(inputValue);
                }}
                className="relative flex items-center"
              >
                <input
                  type="text"
                  value={inputValue}
                  onChange={(e) => setInputValue(e.target.value)}
                  placeholder={userName ? "Ask me anything..." : "Enter your name..."}
                  className="w-full rounded-2xl border border-white/5 bg-zinc-900 px-5 py-3.5 pr-14 text-sm text-white outline-none transition-all focus:border-purple-500/50 focus:bg-zinc-800/50"
                />
                <button
                  type="submit"
                  disabled={!inputValue.trim()}
                  className="absolute right-2 flex h-10 w-10 items-center justify-center rounded-xl bg-purple-600 text-white transition-all hover:scale-105 hover:bg-purple-50 hover:text-purple-600 disabled:opacity-0"
                >
                  <Send size={18} />
                </button>
              </form>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Toggle Button */}
      <div className="relative">
        <motion.button
          whileHover={{ scale: 1.05, y: -2 }}
          whileTap={{ scale: 0.95 }}
          onClick={() => setIsOpen(!isOpen)}
          className={`relative flex h-12 w-12 items-center justify-center rounded-2xl shadow-2xl transition-all duration-500 ${
            isOpen 
              ? "bg-zinc-900 text-white rotate-0" 
              : "bg-gradient-to-tr from-purple-600 to-indigo-600 text-white shadow-purple-500/25"
          }`}
        >
          {isOpen ? (
            <X size={22} />
          ) : (
            <>
              <div className="relative">
                <MessageCircle size={22} />
                {!userName && (
                  <div className="absolute -right-0.5 -top-0.5 flex h-3.5 w-3.5 items-center justify-center rounded-full bg-white text-[9px] font-bold text-purple-600">
                    1
                  </div>
                )}
              </div>
              {/* Online indicator dot on the top right edge */}
              <div className="absolute -top-1 -right-1 flex h-3 w-3 z-10">
                <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-green-400 opacity-75"></span>
                <span className="relative inline-flex h-3 w-3 rounded-full border-2 border-white bg-green-500 shadow-sm"></span>
              </div>
            </>
          )}
        </motion.button>
      </div>
    </div>
  );
}
